import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Box,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Chip,
  Badge,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  MergeType as MergeIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ManageAccounts as ManageAccountsIcon,
  Storage as StorageIcon,
  Email as EmailIcon,
  PendingActions as PendingActionsIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { companiesAPI, personsAPI, accountsAPI } from '../services/api';
import { toast } from 'react-toastify';
import AdminUsers from './AdminUsers';
import AdminInvitations from './AdminInvitations';
import AdminPendingAccounts from './AdminPendingAccounts';
import CompanyEditDialog from '../components/CompanyEditDialog';
import PersonEditDialog from '../components/PersonEditDialog';
import PersonMergeDialog from '../components/PersonMerge/PersonMergeDialog';
import { usePermissions } from '../hooks/useAuth';

const Admin: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [tabValue, setTabValue] = useState(0);
  const [companies, setCompanies] = useState<any[]>([]);
  const [persons, setPersons] = useState<any[]>([]);
  const [openCompany, setOpenCompany] = useState(false);
  const [openPerson, setOpenPerson] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any | null>(null);
  const [editingPerson, setEditingPerson] = useState<any | null>(null);
  const [jurisdictions, setJurisdictions] = useState<Array<{ jurisdiction: string; count: number }>>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [personSearch, setPersonSearch] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  // Merge persons UI state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<any | null>(null);
  const [mergeSources, setMergeSources] = useState<any[]>([]);
  const jurisdictionOptions = useMemo(
    () => (jurisdictions || []).map((j) => j.jurisdiction).filter(Boolean),
    [jurisdictions],
  );

  const load = async () => {
    try {
      const [c, p, j] = await Promise.all([
        companiesAPI.getAll({ page: 1, limit: 100 }),
        personsAPI.getAll({ page: 1, limit: 100 }),
        companiesAPI.getJurisdictions(),
      ]);
      setCompanies(c.data?.data || c.data?.items || c.data || []);
      setPersons(p.data?.data || p.data?.items || p.data || []);
      setJurisdictions(j.data || []);
    } finally {
    }
  };

  useEffect(() => {
    load();
    loadPendingCount();
  }, []);

  const loadPendingCount = async () => {
    try {
      const response = await accountsAPI.getPendingCount();
      setPendingCount(response.data?.count || 0);
    } catch (error) {
      console.error('Failed to load pending count:', error);
    }
  };

  // Debounced search for companies and persons
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        const [c, p] = await Promise.all([
          companiesAPI.getAll({ page: 1, limit: 100, search: companySearch || undefined }),
          personsAPI.getAll({ page: 1, limit: 100, search: personSearch || undefined }),
        ]);
        setCompanies(c.data?.data || c.data?.items || c.data || []);
        setPersons(p.data?.data || p.data?.items || p.data || []);
      } finally {
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [companySearch, personSearch]);

  const handleCompanySave = () => {
    setOpenCompany(false);
    setEditingCompany(null);
    load();
  };

  const handlePersonSave = () => {
    setOpenPerson(false);
    setEditingPerson(null);
    load();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Premium Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: 'white',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <SearchIcon sx={{ fontSize: 36 }} />
          </Avatar>
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Admin Panel
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Manage users, roles, companies, persons, and merge duplicates
            </Typography>
          </Box>
          <Tooltip title="Back to Dashboard">
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Dashboard
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label="admin tabs">
          <Tab
            icon={<StorageIcon />}
            iconPosition="start"
            label="Data Management"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            icon={<ManageAccountsIcon />}
            iconPosition="start"
            label="Users & Roles"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            icon={<EmailIcon />}
            iconPosition="start"
            label="Invitations"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            icon={
              <Badge badgeContent={pendingCount} color="error">
                <PendingActionsIcon />
              </Badge>
            }
            iconPosition="start"
            label="Pending Accounts"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <BusinessIcon />
                  </Avatar>
                  <Typography variant="h6">Companies</Typography>
                  <Badge badgeContent={companies.length} color="primary" max={999}>
                    <Chip label="Total" size="small" />
                  </Badge>
                </Stack>
                {hasPermission('companies:create') && (
                  <Tooltip title="Add new company">
                    <Button variant="contained" onClick={() => { setEditingCompany(null); setOpenCompany(true); }}>Add Company</Button>
                  </Tooltip>
                )}
              </Stack>
              <Divider sx={{ mb: 2 }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search companies by name or registration number"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                    <SearchIcon fontSize="small" />
                  </Box>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <List>
              {(companies || []).map((c: any) => {
                const statusColor = String(c.status).toUpperCase() === 'WOUND_UP' || String(c.status).toUpperCase() === 'DISSOLVED'
                  ? 'error'
                  : String(c.status).toUpperCase() === 'ACTIVE'
                  ? 'success'
                  : 'warning';

                return (
                  <React.Fragment key={c.id}>
                    <ListItemButton
                      component={RouterLink}
                      to={`/companies/${encodeURIComponent(c.id)}`}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>
                          {c.name?.charAt(0) || '?'}
                        </Avatar>
                      </Stack>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body1" fontWeight={500}>
                              {c.name}
                            </Typography>
                            {c.oldName && (
                              <Tooltip title={`Previously: ${c.oldName}`}>
                                <Chip label="Renamed" size="small" variant="outlined" color="info" />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                              {c.jurisdiction && (
                                <Chip
                                  label={c.jurisdiction}
                                  size="small"
                                  sx={{
                                    bgcolor: 'primary.light',
                                    color: 'white',
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                              {c.status && (
                                <Chip
                                  label={c.status}
                                  size="small"
                                  color={statusColor}
                                />
                              )}
                              {c.registrationNumber && (
                                <Tooltip title="Registration Number">
                                  <Chip
                                    label={`Reg: ${c.registrationNumber}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {c.incorporationDate && (
                                <Tooltip title="Incorporation Date">
                                  <Chip
                                    label={new Date(c.incorporationDate).toLocaleDateString()}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                            {c.address && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                📍 {c.address}
                              </Typography>
                            )}
                            {c.sourceSheet && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Source: {c.sourceSheet}
                                {c.sourceRowNumber && ` (Row ${c.sourceRowNumber})`}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                        {hasPermission('companies:update') && (
                          <Tooltip title="Edit company">
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<EditIcon />}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingCompany(c);
                                setOpenCompany(true);
                              }}
                            >
                              Edit
                            </Button>
                          </Tooltip>
                        )}
                        {hasPermission('companies:delete') && (
                          <Tooltip title="Delete company">
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (window.confirm(`Delete company "${c.name}"? This action cannot be undone.`)) {
                                  try {
                                    await companiesAPI.remove(c.id);
                                    toast.success('Company deleted');
                                    load();
                                  } catch (error: any) {
                                    toast.error('Failed to delete company');
                                    console.error(error);
                                  }
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    </ListItemButton>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <PeopleIcon />
                  </Avatar>
                  <Typography variant="h6">Persons</Typography>
                  <Badge badgeContent={persons.length} color="success" max={999}>
                    <Chip label="Total" size="small" />
                  </Badge>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {hasPermission('persons:manage') && (
                    <Tooltip title="Merge duplicate persons">
                      <Button variant="outlined" startIcon={<MergeIcon />} onClick={() => setMergeDialogOpen(true)}>Merge</Button>
                    </Tooltip>
                  )}
                  {hasPermission('persons:create') && (
                    <Tooltip title="Add new person">
                      <Button variant="contained" onClick={() => { setEditingPerson(null); setOpenPerson(true); }}>Add Person</Button>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
              <Divider sx={{ mb: 2 }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search persons by name"
              value={personSearch}
              onChange={(e) => setPersonSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                    <SearchIcon fontSize="small" />
                  </Box>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <List>
              {(persons || []).map((p: any) => (
                <React.Fragment key={p.id}>
                  <ListItemButton
                    component={RouterLink}
                    to={`/persons/${encodeURIComponent(p.id)}`}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'success.main' }}>
                        {p.name?.charAt(0) || '?'}
                      </Avatar>
                    </Stack>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={500}>
                          {p.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            {(p.types || []).map((type: string) => (
                              <Chip
                                key={type}
                                label={type}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                            {p.email && (
                              <Chip
                                label={p.email}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>
                      }
                    />
                    <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                      {hasPermission('persons:update') && (
                        <Tooltip title="Edit person">
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingPerson(p);
                              setOpenPerson(true);
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                      )}
                      {hasPermission('persons:delete') && (
                        <Tooltip title="Delete person">
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (window.confirm(`Delete person "${p.name}"? This action cannot be undone.`)) {
                                try {
                                  await personsAPI.remove(p.id);
                                  toast.success('Person deleted');
                                  load();
                                } catch (error: any) {
                                  toast.error('Failed to delete person');
                                  console.error(error);
                                }
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
            </CardContent>
          </Card>
        </Grid>
        </Grid>
      )}

      {/* Users & Roles Tab */}
      {tabValue === 1 && (
        <Box>
          <AdminUsers />
        </Box>
      )}

      {/* Invitations Tab */}
      {tabValue === 2 && (
        <Box>
          <AdminInvitations />
        </Box>
      )}

      {/* Pending Accounts Tab */}
      {tabValue === 3 && (
        <Box>
          <AdminPendingAccounts />
        </Box>
      )}

      {/* Merge Persons Dialog */}
      <PersonMergeDialog
        open={mergeDialogOpen}
        onClose={() => {
          setMergeDialogOpen(false);
          setMergeTarget(null);
          setMergeSources([]);
        }}
        targetPerson={mergeTarget || undefined}
        sourcePersons={mergeSources}
        onComplete={async () => {
          await load();
        }}
      />
      <CompanyEditDialog
        open={openCompany}
        onClose={() => {
          setOpenCompany(false);
          setEditingCompany(null);
        }}
        company={editingCompany}
        onSave={handleCompanySave}
        jurisdictions={jurisdictionOptions}
        showAdvancedFields={true}
      />

      <PersonEditDialog
        open={openPerson}
        onClose={() => {
          setOpenPerson(false);
          setEditingPerson(null);
        }}
        person={editingPerson}
        onSave={handlePersonSave}
        showAdvancedFields={false}
      />
    </Container>
  );
};

export default Admin;
